import React, { useCallback, useEffect, useState } from 'react';
import {
  BarChart3,
  Copy,
  Link2,
  Lock,
  Mail,
  Share2,
  Trash2,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { shareInviteService, type ShareResourceType } from '@/services/shareInvites';
import {
  appPublicLinkService,
  type AppPublicLink,
} from '@/services/appPublicLinks';
import { toast } from 'sonner';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ShareMode = 'internal' | 'public';

interface ShareResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: ShareResourceType;
  resourceId: string;
  resourceName: string;
  onShared?: () => void;
}

const EXPIRY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: 'Never', value: 0 },
];

export const ShareResourceModal: React.FC<ShareResourceModalProps> = ({
  isOpen,
  onClose,
  resourceType,
  resourceId,
  resourceName,
  onShared,
}) => {
  const [mode, setMode] = useState<ShareMode>('internal');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [publicLinks, setPublicLinks] = useState<AppPublicLink[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [linkPassword, setLinkPassword] = useState('');
  const [allowInteraction, setAllowInteraction] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);

  const resourceLabel = resourceType === 'project' ? 'project' : 'app';
  const supportsPublicLink = resourceType === 'app';

  const loadPublicLinks = useCallback(async () => {
    if (!supportsPublicLink || !resourceId) return;
    setLoadingLinks(true);
    try {
      const links = await appPublicLinkService.list(resourceId);
      setPublicLinks(links);
    } catch {
      setPublicLinks([]);
    } finally {
      setLoadingLinks(false);
    }
  }, [resourceId, supportsPublicLink]);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setError(null);
      setSubmitting(false);
      setMode('internal');
      setLinkPassword('');
      setAllowInteraction(false);
      setExpiresInDays(7);
      setPublicLinks([]);
      return;
    }
    if (supportsPublicLink) {
      void loadPublicLinks();
    }
  }, [isOpen, supportsPublicLink, loadPublicLinks]);

  const handleInternalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await shareInviteService.invite(resourceType, resourceId, trimmed, 'viewer');
      toast.success(`Invitation sent to ${trimmed}`);
      onShared?.();
      onClose();
    } catch (err) {
      const msg = (err as Error)?.message ?? 'Failed to send invitation';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePublicLink = async () => {
    setError(null);
    setCreatingLink(true);
    try {
      const link = await appPublicLinkService.create(resourceId, {
        expires_in_days: expiresInDays,
        password: linkPassword.trim() || undefined,
        allow_interaction: allowInteraction,
      });
      setPublicLinks((prev) => [link, ...prev]);
      setLinkPassword('');
      toast.success('Public link created');
      onShared?.();
    } catch (err) {
      const msg = (err as Error)?.message ?? 'Failed to create public link';
      setError(msg);
      toast.error(msg);
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleRevokeLink = async (linkId: string) => {
    try {
      await appPublicLinkService.revoke(resourceId, linkId);
      setPublicLinks((prev) =>
        prev.map((l) => (l.id === linkId ? { ...l, status: 'revoked' as const } : l))
      );
      toast.success('Link revoked');
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Failed to revoke link');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Share ${resourceName}`} size="md">
      {supportsPublicLink && (
        <div className="flex gap-1 p-1 mb-4 rounded-lg bg-khiimori-bg-elevated/10 border border-khiimori-border/10">
          <button
            type="button"
            onClick={() => setMode('internal')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-md transition-colors ${
              mode === 'internal'
                ? 'bg-primary-500/20 text-primary-300'
                : 'text-khiimori-text-muted hover:text-khiimori-text-primary'
            }`}
          >
            <Mail className="w-4 h-4" />
            Internal
          </button>
          <button
            type="button"
            onClick={() => setMode('public')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-md transition-colors ${
              mode === 'public'
                ? 'bg-primary-500/20 text-primary-300'
                : 'text-khiimori-text-muted hover:text-khiimori-text-primary'
            }`}
          >
            <Link2 className="w-4 h-4" />
            Public link
          </button>
        </div>
      )}

      {mode === 'internal' && (
        <form onSubmit={handleInternalSubmit} className="space-y-4">
          <p className="text-sm text-khiimori-text-muted">
            Send an email invite so a teammate can access this {resourceLabel}. Invitations expire
            after 7 days.
          </p>

          <label className="block">
            <span className="text-sm font-medium text-khiimori-text-primary flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4" /> Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="collaborator@example.com"
              autoFocus
              required
              className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            />
          </label>

          {error && (
            <p className="text-sm text-red-500" role="alert">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="gap-1.5">
              <Share2 className="w-4 h-4" />
              {submitting ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </form>
      )}

      {mode === 'public' && supportsPublicLink && (
        <div className="space-y-5">
          <p className="text-sm text-khiimori-text-muted">
            Generate a URL anyone can open. Optionally set an expiry, password, and whether viewers
            can interact.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-2 sm:col-span-1">
              <span className="text-sm font-medium text-khiimori-text-primary mb-1 block">
                Expires
              </span>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block col-span-2 sm:col-span-1">
              <span className="text-sm font-medium text-khiimori-text-primary flex items-center gap-2 mb-1">
                <Lock className="w-4 h-4" /> Password (optional)
              </span>
              <input
                type="password"
                value={linkPassword}
                onChange={(e) => setLinkPassword(e.target.value)}
                placeholder="Leave empty for open access"
                className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              />
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm text-khiimori-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={allowInteraction}
              onChange={(e) => setAllowInteraction(e.target.checked)}
              className="rounded border-khiimori-border"
            />
            Allow viewers to interact with the app
          </label>

          {error && (
            <p className="text-sm text-red-500" role="alert">
              {error}
            </p>
          )}

          <Button
            type="button"
            onClick={() => void handleCreatePublicLink()}
            disabled={creatingLink}
            className="gap-1.5"
          >
            <Link2 className="w-4 h-4" />
            {creatingLink ? 'Creating…' : 'Create public link'}
          </Button>

          <div className="border-t border-khiimori-border/10 pt-4">
            <h3 className="text-sm font-medium text-khiimori-text-primary mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Active links
            </h3>
            {loadingLinks ? (
              <p className="text-sm text-khiimori-text-muted">Loading links…</p>
            ) : publicLinks.length === 0 ? (
              <p className="text-sm text-khiimori-text-muted">No public links yet.</p>
            ) : (
              <ul className="space-y-3 max-h-56 overflow-y-auto">
                {publicLinks.map((link) => (
                  <li
                    key={link.id}
                    className="p-3 rounded-lg border border-khiimori-border/10 bg-khiimori-bg-elevated/5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-khiimori-text-muted truncate">{link.url}</p>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-khiimori-text-muted">
                          <span
                            className={
                              link.status === 'active'
                                ? 'text-success-400'
                                : 'text-khiimori-text-muted'
                            }
                          >
                            {link.status}
                          </span>
                          {link.has_password && <span>password protected</span>}
                          {link.allow_interaction && <span>interaction on</span>}
                          {link.expires_at && (
                            <span>expires {new Date(link.expires_at).toLocaleDateString()}</span>
                          )}
                        </div>
                        <p className="text-xs text-khiimori-text-muted mt-1">
                          {link.analytics.views} views · {link.analytics.interactions} interactions
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {link.status === 'active' && (
                          <>
                            <button
                              type="button"
                              onClick={() => void handleCopyLink(link.url)}
                              className="p-1.5 rounded hover:bg-khiimori-bg-elevated/20 text-khiimori-text-muted hover:text-khiimori-text-primary"
                              aria-label="Copy link"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleRevokeLink(link.id)}
                              className="p-1.5 rounded hover:bg-error-500/10 text-khiimori-text-muted hover:text-error-400"
                              aria-label="Revoke link"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ShareResourceModal;
